import { useState, useRef, useCallback, useEffect } from 'react';

const SpeechRecognitionAPI =
  typeof window !== 'undefined' &&
  (window.SpeechRecognition || window.webkitSpeechRecognition);

/**
 * Hook for browser speech-to-text via Web Speech API.
 * Supports Chrome, Edge, Safari (webkit). Firefox is not supported.
 *
 * @param {Object} options
 * @param {string} [options.lang='pl-PL'] - Recognition language
 * @param {(text: string) => void} options.onResult - Called with final or appended text
 * @param {boolean} [options.append=true] - If true, onResult receives currentValue + new transcript
 * @param {string} [options.currentValue=''] - Current field value (used when append is true)
 * @param {boolean} [options.singleShot=false] - If true, one utterance per start (continuous: false). Use on mobile to reduce duplication.
 * @param {(transcript: string) => void} [options.onPreview] - If set, called with the new transcript instead of onResult; consumer can show a dialog and then call onResult after user confirms/edits.
 * @returns {{ startListening: () => void, stopListening: () => void, isListening: boolean, isSupported: boolean, error: string | null }}
 */
export function useSpeechToText({
  lang = 'pl-PL',
  onResult,
  append = true,
  currentValue = '',
  singleShot = false,
  onPreview = null
}) {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState(null);
  const recognitionRef = useRef(null);
  const currentValueRef = useRef(currentValue);

  currentValueRef.current = currentValue;

  const isSupported = Boolean(SpeechRecognitionAPI);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) return;
    try {
      recognitionRef.current.stop();
    } catch (_) {
      // ignore
    }
    recognitionRef.current = null;
    setIsListening(false);
  }, []);

  const startListening = useCallback(() => {
    if (!SpeechRecognitionAPI || !onResult) return;
    setError(null);

    if (recognitionRef.current) {
      stopListening();
      return;
    }

    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = !singleShot;
    recognition.interimResults = !singleShot;
    recognition.lang = lang;

    // Accumulate latest full transcript; only report once in onend (some engines fire onend multiple times)
    const latestTranscriptRef = { current: '' };
    const reportedRef = { current: false };
    // Debounce: skip if the same transcript is sent twice in a row (mobile engines often do this)
    const lastReportedRef = { current: '' };

    // Normalize and deduplicate transcript (device/engine specific - some triple the first word or repeat phrases)
    const normalizeTranscript = (text) => {
      const normalized = text.trim().replace(/\s+/g, ' ');
      if (!normalized) return '';

      let words = normalized.split(' ');
      // 1) Remove consecutive duplicate words (e.g. "hello hello world" -> "hello world")
      const noConsecDupes = [];
      let prev = '';
      for (const w of words) {
        if (w !== prev) {
          noConsecDupes.push(w);
          prev = w;
        }
      }
      words = noConsecDupes;

      // 2) Remove repeated leading phrase (e.g. "hello world hello world" or engine re-sending same start)
      while (words.length >= 2) {
        let found = false;
        for (let n = 1; n <= Math.floor(words.length / 2); n++) {
          const first = words.slice(0, n).join(' ');
          const next = words.slice(n, 2 * n).join(' ');
          if (first === next) {
            words = words.slice(n);
            found = true;
            break;
          }
        }
        if (!found) break;
      }

      return words.join(' ');
    };

    recognition.onresult = (event) => {
      let fullTranscript = '';
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? '';
        const confidence = result[0]?.confidence;
        // On mobile, false finals often have confidence 0; only add when isFinal and (confidence > 0 or undefined for desktop)
        if (!result.isFinal) {
          fullTranscript += transcript;
        } else if (confidence === undefined || confidence > 0) {
          fullTranscript += transcript;
        }
      }
      latestTranscriptRef.current = fullTranscript.trim();
    };

    recognition.onend = () => {
      if (!reportedRef.current) {
        reportedRef.current = true;
        const newPart = normalizeTranscript(latestTranscriptRef.current);
        // Skip duplicate: same transcript sent twice in rapid succession (common on mobile)
        if (newPart && newPart !== lastReportedRef.current) {
          lastReportedRef.current = newPart;
          if (onPreview) {
            onPreview(newPart);
          } else {
            const base = append ? (currentValueRef.current || '').trim() : '';
            const newText = base ? `${base} ${newPart}` : newPart;
            onResult(newText);
          }
        }
      }
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognition.onerror = (event) => {
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        setError('Brak dostępu do mikrofonu');
      } else if (event.error === 'no-speech') {
        setError('Nie wykryto mowy');
      } else if (event.error === 'network') {
        setError('Błąd sieci');
      } else {
        setError(event.error || 'Błąd rozpoznawania');
      }
      recognitionRef.current = null;
      setIsListening(false);
    };

    try {
      recognition.start();
      recognitionRef.current = recognition;
      setIsListening(true);
    } catch (err) {
      setError('Nie udało się uruchomić rozpoznawania mowy');
      setIsListening(false);
    }
  }, [lang, onResult, append, singleShot, onPreview, stopListening]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (_) {}
        recognitionRef.current = null;
      }
      setIsListening(false);
    };
  }, []);

  return {
    startListening,
    stopListening,
    isListening,
    isSupported,
    error
  };
}
